/**
 * RestoreVersionsModal — Shows files changed by an AI message with version restore options
 *
 * Displays a list of files that were modified, created, or deleted by a specific
 * assistant message, with version numbers and dates. Users can select individual
 * files or all files to restore to their pre-/post-message versions.
 *
 * The version store is the only restoration source (#768). Each row is what the
 * server's `planRestore` decided — the SAME decision `POST /restore-versions`
 * acts on — in one of three states, recognisable at a glance:
 *
 *   v12 → v4                               exact version, unchanged from before
 *   v12 → ~~v4~~ v3   older version pruned  substitute: the requested version
 *                                           was pruned; restores to the closest
 *                                           kept one that still predates the
 *                                           change. Ticked by default.
 *   v5 from Bin → v5                       the file is in the Bin (the AI
 *                                           deleted it): restored from there
 *   file no longer exists   skipped        not restorable; unticked and not
 *                                           tickable
 *
 * There is no "restore to content before this message" row any more: that
 * described restoring from text stored in the conversation, which is no longer
 * stored.
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { History, FileText, FilePlus, FileX, Check, X, Loader2, AlertCircle, ArrowRight } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { request, errorText } from '@papan/api-client'
import { UNAVAILABLE_COPY, type UnavailableReason } from '../core/restore-target.js'

interface VersionInfo {
  id: string
  number: number
  date: string
  source: string
  description?: string
}

/** One row of `GET …/message/:id/versions`. */
export interface VersionSnapshot {
  path: string
  action: string
  fileId?: string
  /** The server's `RestorePlan` kind. */
  /** `move` (#1062 step C): the item kept its id and moved — a restore moves it back to `fromPath`. */
  plan: 'delete' | 'version' | 'bin' | 'missing' | 'unavailable' | 'move'
  /** A `move` row: where the item goes back to. */
  fromPath?: string
  /** Why an `unavailable` row cannot be restored. Optional: an older API
   *  omits it, and the row then reads "no kept version". */
  reason?: UnavailableReason | null
  /** Newest kept version — what the file is now (in the Bin: when deleted). */
  current: VersionInfo | null
  /** What a restore writes (plan = version). */
  target: VersionInfo | null
  /** `target` is a substitute: the requested version was pruned. */
  pruned: boolean
  /** The version that was asked for, when known. */
  requestedNumber: number | null
  totalVersions: number
  /** Already in the target state — a delete target already gone. */
  unchanged?: boolean
}

/** One entry of `POST …/restore-versions`' `restored[]`. */
export interface RestoredFile {
  path: string
  requested: number | null
  restoredTo: number | null
  pruned: boolean
  /** Brought back from the Bin. */
  fromBin?: boolean
  /** The file was already in the target state — nothing changed. */
  unchanged?: boolean
}

interface Props {
  sessionId: string
  messageId: string
  workspaceId?: string
  onClose: () => void
  onRestored?: (restored: RestoredFile[]) => void
  /** When true, button says "Restore N file(s) and send" */
  andSend?: boolean
  /** 'before' restores to pre-message state, 'after' restores to post-message state */
  mode?: 'before' | 'after'
}

/** Whether a row can be restored at all (the other two plans are skipped). */
export function isRestorable(snap: Pick<VersionSnapshot, 'plan'>): boolean {
  return snap.plan === 'version' || snap.plan === 'delete' || snap.plan === 'bin' || snap.plan === 'move'
}

/**
 * The result sentence — "Restored 5 files (1 from the Bin), 1 to an older
 * pruned version; 1 already matched" — used by the modal and by both callers'
 * toasts, so a substitute is never reported as an exact restore, and a file
 * that was already in the target state is never counted as restored.
 */
export function describeRestored(restored: readonly Pick<RestoredFile, 'pruned' | 'fromBin' | 'unchanged'>[]): string {
  const changed = restored.filter(r => !r.unchanged)
  const unchanged = restored.length - changed.length
  const matched = `${unchanged} file${unchanged !== 1 ? 's' : ''} already matched`
  if (changed.length === 0) return `Nothing to restore — ${matched}`
  const n = changed.length
  const pruned = changed.filter(r => r.pruned).length
  const fromBin = changed.filter(r => r.fromBin).length
  let text = `Restored ${n} file${n !== 1 ? 's' : ''}${fromBin > 0 ? ` (${fromBin} from the Bin)` : ''}`
  if (pruned > 0) text += `, ${pruned} to an older pruned version`
  return unchanged > 0 ? `${text}; ${unchanged} already matched` : text
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.floor(diffMs / 60_000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`

  // Older than a week — show date
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: now.getFullYear() !== d.getFullYear() ? 'numeric' : undefined })
}

function VersionBadge({ version }: { version: VersionInfo | null }) {
  if (!version) return <span className="themed-text-muted">—</span>
  return (
    <span className="inline-flex items-center gap-1" title={`${version.description ?? version.source} — ${new Date(version.date).toLocaleString()}`}>
      <span className="font-mono font-medium">v{version.number}</span>
      <span className="opacity-50">{formatDate(version.date)}</span>
    </span>
  )
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  created: <FilePlus size={14} className="text-green-400" />,
  updated: <FileText size={14} className="text-blue-400" />,
  deleted: <FileX size={14} className="text-red-400" />,
  moved: <ArrowRight size={14} className="text-amber-400" />,
}

const ACTION_LABELS: Record<string, string> = {
  created: 'Created by AI',
  updated: 'Modified by AI',
  deleted: 'Deleted by AI',
  moved: 'Moved by AI',
}

/** The line under a row's path: where the file goes, or why it is skipped. */
function RowOutcome({ snap }: { snap: VersionSnapshot }) {
  if (snap.plan === 'missing' || snap.plan === 'unavailable') {
    return (
      <div className="flex items-center gap-1.5 mt-1 text-[11px] themed-text-muted" data-testid="restore-row-skipped">
        <span>{snap.plan === 'missing' ? 'file no longer exists' : UNAVAILABLE_COPY[snap.reason ?? 'no-versions']}</span>
        <span className="opacity-60">skipped</span>
      </div>
    )
  }
  if (snap.plan === 'move') {
    return (
      <div className="flex items-center gap-1.5 mt-1 text-[11px] themed-text-muted" data-testid="restore-row-move">
        <span className="opacity-50">{snap.unchanged ? 'Already back in place — nothing to change' : `Will move back to ${snap.fromPath ?? 'where it was'}`}</span>
      </div>
    )
  }
  if (snap.plan === 'delete') {
    return (
      <div className="flex items-center gap-1.5 mt-1 text-[11px] themed-text-muted">
        <span className="opacity-50">{snap.unchanged ? 'Already deleted — nothing to change' : 'Will delete this file'}</span>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-1.5 mt-1 text-[11px] themed-text-muted" data-testid={snap.pruned ? 'restore-row-pruned' : 'restore-row-exact'}>
      <span className="font-mono font-medium">v{snap.current?.number ?? snap.totalVersions}</span>
      {snap.plan === 'bin' && <span data-testid="restore-row-bin">from Bin</span>}
      <ArrowRight size={10} className="opacity-40 flex-shrink-0" />
      {snap.pruned && snap.requestedNumber != null && (
        <span className="font-mono line-through opacity-60" data-testid="restore-row-requested">v{snap.requestedNumber}</span>
      )}
      <VersionBadge version={snap.target} />
      {snap.pruned && <span className="text-amber-400/80">older version pruned</span>}
    </div>
  )
}

export function RestoreVersionsModal({ sessionId, messageId, workspaceId, onClose, onRestored, andSend, mode = 'before' }: Props) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  const [snapshots, setSnapshots] = useState<VersionSnapshot[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [restoring, setRestoring] = useState(false)
  const [result, setResult] = useState<{ restored: RestoredFile[]; failed: Array<{ path: string; reason: string }> } | null>(null)

  const wsQuery = workspaceId ? `?workspaceId=${workspaceId}` : ''
  const modeQuery = `${wsQuery ? '&' : '?'}mode=${mode}`

  // Fetch version metadata. Every restorable row — exact AND substitute — starts
  // ticked; a skipped row never is.
  useEffect(() => {
    setLoading(true)
    setError(null)
    request<{ snapshots: VersionSnapshot[] }>(
      `/api/agent/sessions/${sessionId}/message/${messageId}/versions${wsQuery}${modeQuery}`,
    )
      .then(data => {
        setSnapshots(data.snapshots)
        setSelected(new Set(data.snapshots.filter(isRestorable).map(s => s.path)))
      })
      .catch(err => { const text = errorText(err, 'Failed to load version data'); if (text !== null) setError(text) })   // #1316
      .finally(() => setLoading(false))
  }, [sessionId, messageId, wsQuery, modeQuery])

  const restorable = useMemo(() => snapshots.filter(isRestorable), [snapshots])

  const toggleFile = useCallback((path: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    if (selected.size === restorable.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(restorable.map(s => s.path)))
    }
  }, [selected.size, restorable])

  const handleRestore = useCallback(async () => {
    if (selected.size === 0) return
    setRestoring(true)
    try {
      const data = await request<{ ok: boolean; restored: RestoredFile[]; failed: Array<{ path: string; reason: string }> }>(
        `/api/agent/sessions/${sessionId}/restore-versions${wsQuery}`,
        {
          method: 'POST',
          body: JSON.stringify({
            messageId,
            files: [...selected],
            restoreTo: mode,
          }),
        },
      )
      setResult(data)
      // Edit & resend SENDS from `onRestored` and closes the modal — so with
      // any failure, stop here and show it; the user sends from the result
      // (review 2026-09-23: "Restored 1 file" hid two refused ones).
      if (data.restored.length > 0 && !(andSend && data.failed.length > 0)) {
        onRestored?.(data.restored)
      }
    } catch (err: any) {
      const text = errorText(err, 'Restore failed')   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
    } finally {
      setRestoring(false)
    }
  }, [sessionId, messageId, wsQuery, selected, onRestored, mode, andSend])

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className={`${themeClass} relative z-[9999] w-full max-w-xl mx-4 themed-bg-secondary themed-border border rounded-lg shadow-2xl`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <div className="flex items-center gap-2">
            <History size={16} className="themed-text-muted" />
            <h2 className="text-sm font-medium themed-text">{mode === 'after' ? 'Restore version of this' : 'Restore versions'}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary">
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3 max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin themed-text-muted" />
            </div>
          )}

          {error && !result && (
            <div className="flex items-center gap-2 py-4 text-red-400 text-xs">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {!loading && !error && snapshots.length === 0 && (
            <p className="text-xs themed-text-muted py-4 text-center">
              No file changes found for this message.
            </p>
          )}

          {result && (
            <div className="py-4 space-y-2">
              {result.restored.length > 0 && (
                <div className="text-xs text-green-400 flex items-center gap-1.5">
                  <Check size={14} />
                  {describeRestored(result.restored)}
                </div>
              )}
              {result.failed.length > 0 && (
                <div className="text-xs text-red-400 flex items-start gap-1.5">
                  <AlertCircle size={14} className="shrink-0 mt-px" />
                  <span>Failed to restore: {result.failed.map(f => `${f.path} (${f.reason})`).join(', ')}</span>
                </div>
              )}
            </div>
          )}

          {!loading && !result && snapshots.length > 0 && (
            <>
              {restorable.length === 0 && (
                // Say WHY nothing is restorable. "Before version tracking" is
                // true only when no row keeps any version; a file deleted since
                // (plan = missing) is a different story with a different fix —
                // the Bin. Showing the tracking note for a minutes-old message
                // was caught validating #768 in the running app.
                <p className="text-xs themed-text-muted mb-3 flex items-center gap-1.5" data-testid="restore-none-note">
                  <AlertCircle size={12} className="shrink-0" />
                  {snapshots.every(s => s.plan === 'unavailable' && (!s.reason || s.reason === 'no-versions' || s.reason === 'not-recorded'))
                    ? 'Version details not available for this message (created before version tracking).'
                    : `Nothing here can be restored.${snapshots.some(s => s.plan === 'missing' || s.reason === 'bin-folder-gone') ? ' Deleted files can be recovered from the Bin.' : ''}`}
                </p>
              )}

              {/* Select all — over the rows that CAN be restored */}
              {restorable.length > 0 && (
                <label className="flex items-center gap-2 mb-2 cursor-pointer text-xs themed-text-muted hover:themed-text">
                  <input
                    type="checkbox"
                    checked={selected.size === restorable.length}
                    onChange={toggleAll}
                    className="rounded"
                  />
                  Select all ({restorable.length} file{restorable.length !== 1 ? 's' : ''})
                </label>
              )}

              {/* File list */}
              <div className="space-y-1">
                {snapshots.map(snap => {
                  const canRestore = isRestorable(snap)
                  return (
                    <label
                      key={snap.path}
                      data-testid="restore-row"
                      className={`flex items-center gap-2 px-2 py-2 rounded transition-colors ${canRestore ? 'cursor-pointer hover:themed-bg-tertiary' : 'opacity-60 cursor-not-allowed'}`}
                    >
                      <input
                        type="checkbox"
                        checked={canRestore && selected.has(snap.path)}
                        disabled={!canRestore}
                        onChange={() => toggleFile(snap.path)}
                        // Path AND provenance: this row exists because of
                        // WHAT the AI did to the file ("Modified by AI"), so a
                        // screen reader must hear it as well as the path.
                        aria-label={`${snap.path} ${ACTION_LABELS[snap.action] ?? snap.action}`}
                        className="rounded flex-shrink-0 mt-0.5 self-start"
                      />
                      <div className="flex-1 min-w-0">
                        {/* File path */}
                        <div className="flex items-center gap-1.5">
                          {ACTION_ICONS[snap.action] ?? <FileText size={14} />}
                          <span className="text-xs themed-text truncate" title={snap.path}>
                            {snap.path}
                          </span>
                          <span className="text-[10px] themed-text-muted flex-shrink-0">
                            {ACTION_LABELS[snap.action] ?? snap.action}
                          </span>
                        </div>
                        <RowOutcome snap={snap} />
                      </div>
                    </label>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer. In edit & resend it renders even with NO rows (a turn whose
            changes cancelled out, or a failed load): "Send without restoring"
            is then the only way to send the edit, and closing the modal
            discards it (review 2026-09-23). */}
        {!loading && !result && (snapshots.length > 0 || andSend) && (
          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t themed-border">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
            >
              Cancel
            </button>
            {/* Edit & resend must never be a dead end. The message is sent from
                `onRestored`; when nothing here can be restored (a file deleted
                since, no kept version) the Restore button is disabled, and
                Cancel drops the edit — so without this the edited message
                could not be sent at all (review 2026-09-23). */}
            {andSend && (
              <button
                onClick={() => onRestored?.([])}
                disabled={restoring}
                className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-50 transition-colors"
              >
                Send without restoring
              </button>
            )}
            {snapshots.length > 0 && (
              <button
                onClick={handleRestore}
                disabled={selected.size === 0 || restoring}
                className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              >
                {restoring ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <History size={12} />
                    Restore {selected.size} file{selected.size !== 1 ? 's' : ''}{andSend ? ' and send' : ''}
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Close button after result */}
        {result && (
          <div className="flex justify-end gap-2 px-4 py-3 border-t themed-border">
            {/* Some selected file failed: `onRestored` (which sends) was held
                back so the failure is seen. Offer the send rather than
                stranding the edit. */}
            {andSend && result.failed.length > 0 && (
              <button
                onClick={() => onRestored?.(result.restored)}
                className="px-3 py-1.5 text-xs rounded themed-btn-secondary transition-colors"
              >
                {result.restored.length === 0 ? 'Send anyway' : 'Send'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
